'use client';

import { useState } from 'react';
import {
  ArrowLeft,
  LayoutDashboard,
  BookOpen,
  FileText,
  Receipt,
  PiggyBank,
  TrendingUp,
} from 'lucide-react';
import { AccountsDashboard } from './components/AccountsDashboard';
import { ChartOfAccountsPage } from './components/ChartOfAccounts';
import { JournalEntryListPage } from './components/JournalEntryList';
import { PaymentListPage } from './components/PaymentList';
import { BalanceSheetPage } from './components/BalanceSheet';
import { IncomeStatementPage } from './components/IncomeStatement';

export type AccountsPage =
  | 'dashboard'
  | 'chart'
  | 'journal'
  | 'payments'
  | 'reconciliation'
  | 'balance-sheet'
  | 'income-statement';

interface AccountsModuleProps {
  onBack?: () => void;
}

const menuItems: { id: AccountsPage; label: string; icon: typeof LayoutDashboard }[] = [
  { id: 'dashboard', label: '대시보드', icon: LayoutDashboard },
  { id: 'chart', label: '계정과목', icon: BookOpen },
  { id: 'journal', label: '분개장', icon: FileText },
  { id: 'payments', label: '결제', icon: Receipt },
  { id: 'balance-sheet', label: '대차대조표', icon: PiggyBank },
  { id: 'income-statement', label: '손익계산서', icon: TrendingUp },
];

export function AccountsModule({ onBack }: AccountsModuleProps) {
  const [currentPage, setCurrentPage] = useState<AccountsPage>('dashboard');

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <AccountsDashboard onNavigate={setCurrentPage} />;
      case 'chart':
        return <ChartOfAccountsPage />;
      case 'journal':
        return <JournalEntryListPage />;
      case 'payments':
        return <PaymentListPage />;
      case 'balance-sheet':
        return <BalanceSheetPage />;
      case 'income-statement':
        return <IncomeStatementPage />;
      case 'reconciliation':
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-6xl mb-4">🏦</div>
              <h2 className="text-2xl font-bold mb-2">은행 조정</h2>
              <p className="text-muted-foreground">이 기능은 준비 중입니다.</p>
            </div>
          </div>
        );
      default:
        return <AccountsDashboard onNavigate={setCurrentPage} />;
    }
  };

  return (
    <div className="flex flex-col h-full -m-6">
      {/* 상단 네비게이션 */}
      <div className="flex items-center gap-2 px-4 py-3 bg-card border-b border-border">
        {onBack && (
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-accent"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <h1 className="text-xl font-bold mr-6">회계 관리</h1>

        {/* 탭 메뉴 */}
        <div className="flex items-center gap-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setCurrentPage(item.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 페이지 컨텐츠 */}
      <div className="flex-1 overflow-auto p-6">
        {renderPage()}
      </div>
    </div>
  );
}
